import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  Team,
  TeamCreate,
  TeamUpdate,
  TeamWithCount,
} from "@/api/types";

const KEY = ["teams"] as const;

interface UseTeamsFilters {
  include_inactive?: boolean;
}

export function useTeams(filters: UseTeamsFilters = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () =>
      api.GET<TeamWithCount[]>("/teams", {
        params: filters.include_inactive ? { include_inactive: true } : {},
      }),
  });
}

export function useTeam(teamId: string) {
  return useQuery({
    queryKey: [...KEY, teamId],
    queryFn: () => api.GET<Team>(`/teams/${teamId}`),
    enabled: Boolean(teamId),
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TeamCreate) => api.POST<Team>("/teams", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: TeamUpdate) => api.PATCH<Team>(`/teams/${teamId}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: [...KEY, teamId] });
    },
  });
}

export function useDeactivateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) => api.DELETE<void>(`/teams/${teamId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useReactivateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) =>
      api.POST<Team>(`/teams/${teamId}/reactivate`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
